import React, { useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import type { Layout } from 'react-grid-layout';
import ReactGridLayout, { useContainerWidth } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import type { DashboardGridItem } from './dashboardGridLayout';
import {
    DASHBOARD_GRID_COLS,
    DASHBOARD_GRID_GAP,
    DASHBOARD_GRID_MIN_H,
    DASHBOARD_GRID_MIN_W,
    DASHBOARD_GRID_ROW_HEIGHT,
    gridItemHeightPx,
    gridReadingOrder,
} from './dashboardGridLayout';
import { DASHBOARD_DRAG_HANDLE_CLASS } from './DashboardWidget';

// Below this the boxes stack in one column, in reading order, each at its own height: twelve
// columns on a phone would make every chart a sliver.
export const DASHBOARD_GRID_STACK_BELOW = 900;

type Props = {
    items: DashboardGridItem[];
    // One widget, filling the box it is given; `widthPct` is its share of the dashboard width.
    renderItem: (item: DashboardGridItem, widthPct: number) => React.ReactNode;
    // Dragging and resizing are on. Changes are reported once a drag or resize ends.
    editing?: boolean;
    onChange?: (items: DashboardGridItem[]) => void;
};

const fromLayout = (layout: Layout): DashboardGridItem[] =>
    layout.map(({ i, x, y, w, h }) => ({ widgetId: i, x, y, w, h }));

// The dashboard's widgets as boxes on a twelve-column grid.
const DashboardGrid: React.FC<Props> = ({ items, renderItem, editing = false, onChange }) => {
    const { width, containerRef, mounted } = useContainerWidth();
    const layout = useMemo<Layout>(
        () =>
            items.map(it => ({
                i: it.widgetId,
                x: it.x,
                y: it.y,
                w: it.w,
                h: it.h,
                minW: DASHBOARD_GRID_MIN_W,
                minH: DASHBOARD_GRID_MIN_H,
            })),
        [items],
    );
    // Only a finished drag or resize is a change: react-grid-layout also reports the layout
    // when it first packs the boxes, and taking that would turn merely viewing a dashboard
    // into editing it.
    const commit = (next: Layout) => onChange?.(fromLayout(next));

    const stacked = mounted && width < DASHBOARD_GRID_STACK_BELOW;
    return (
        <Box
            ref={containerRef}
            sx={{
                '& .react-grid-item.react-grid-placeholder': {
                    bgcolor: 'primary.main',
                    opacity: 0.15,
                    borderRadius: 3,
                },
                '& .react-grid-item > .react-resizable-handle::after': {
                    borderColor: 'text.secondary',
                },
                // Always show the resize corner while editing, not only on hover.
                ...(editing
                    ? { '& .react-grid-item > .react-resizable-handle': { opacity: 1 } }
                    : {}),
            }}
        >
            {stacked && (
                <Stack gap={`${DASHBOARD_GRID_GAP}px`}>
                    {gridReadingOrder(items).map(item => (
                        <Box key={item.widgetId} sx={{ height: gridItemHeightPx(item.h) }}>
                            {renderItem(item, 100)}
                        </Box>
                    ))}
                </Stack>
            )}
            {mounted && !stacked && (
                <ReactGridLayout
                    layout={layout}
                    width={width}
                    gridConfig={{
                        cols: DASHBOARD_GRID_COLS,
                        rowHeight: DASHBOARD_GRID_ROW_HEIGHT,
                        margin: [DASHBOARD_GRID_GAP, DASHBOARD_GRID_GAP],
                        containerPadding: [0, 0],
                    }}
                    dragConfig={{ enabled: editing, handle: `.${DASHBOARD_DRAG_HANDLE_CLASS}` }}
                    resizeConfig={{ enabled: editing, handles: ['se'] }}
                    onDragStop={commit}
                    onResizeStop={commit}
                >
                    {items.map(item => (
                        <div key={item.widgetId}>
                            {renderItem(item, (item.w / DASHBOARD_GRID_COLS) * 100)}
                        </div>
                    ))}
                </ReactGridLayout>
            )}
        </Box>
    );
};

export default DashboardGrid;
